/* eslint-disable react/no-array-index-key */

import { classnames } from 'tailwindcss-classnames';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { MissingDataSource } from 'components/helpers/EditingHelpText';
import { Routes } from '.generated/templates/Project.Routes.model';
import { DateTime } from 'luxon';
import { LayoutPageSectionShell } from 'components/authorable/layout/LayoutPageSection/LayoutPageSection';
import {
  PageTitleFieldProps,
  PageTitleWithField,
} from 'components/authorable/Heroes/PageTitle/PageTitle';
import RichTextA11yWrapper from 'components/helpers/RichTextA11yWrapper';
import { TextWithOptionalLineBreaks } from 'components/helpers/Heading';
import ReactTooltip from 'react-tooltip';
import useDictionary from 'lib/sitecore/use-dictionary';
import React from 'react';
import { ContentDataLayerObject, gtmDatalayerPush } from 'lib/util/gtm-datalayer';

export type AnnouncementV2PageProps = Routes.Fields.AnnouncementV2;

type AudienceProps = {
  fields: {
    centerType: { name: string };
    salesForceRole: { value: string };
    region: {
      fields: {
        regionName: { value: string };
      };
    };
  };
};

type ProcessedAudienceProps = {
  centerTypeName: string;
  data: Array<{ salesForceRole: string; regionNameValue: string }>;
};

const AnnouncementV2Page = (): React.JSX.Element => {
  const dictionary = useDictionary();
  const { sitecoreContext } = useSitecoreContext();
  const { fields } = sitecoreContext.route as unknown as AnnouncementV2PageProps;
  if (!fields) {
    return <MissingDataSource />; // No fields? Exit early
  }

  const domesticAudiences = fields?.['domestic audiences'] as [];
  const internationalAudiences = fields?.['international audiences'] as [];

  if (!domesticAudiences && !internationalAudiences) {
    return <></>;
  }
  const audiences = [
    ...(domesticAudiences ?? []),
    ...(internationalAudiences ?? []),
  ] as AudienceProps[];

  if (!audiences) {
    return <></>;
  }

  const publishDate = DateTime.fromISO(fields.PublishDate.value).toFormat('LLL dd, yyyy');

  const findExistingObjectIndex = (
    resArray: Array<ProcessedAudienceProps>,
    centerTypeName: string
  ) => {
    return resArray.findIndex(
      (obj: ProcessedAudienceProps) => obj.centerTypeName === centerTypeName
    );
  };
  const resultArray = audiences.reduce(
    (acc: Array<ProcessedAudienceProps>, obj: AudienceProps): Array<ProcessedAudienceProps> => {
      const centerTypeName = obj?.fields?.centerType?.name;
      const salesForceRole = obj?.fields?.salesForceRole?.value;
      const regionName = obj?.fields?.region?.fields?.regionName?.value;
      const existingIndex = findExistingObjectIndex(acc, centerTypeName);
      if (existingIndex !== -1) {
        acc[existingIndex].data.push({
          salesForceRole: salesForceRole,
          regionNameValue: regionName,
        });
      } else {
        acc.push({
          centerTypeName,
          data: [{ salesForceRole: salesForceRole, regionNameValue: regionName }],
        });
      }
      return acc;
    },
    []
  );

  const getAudienceData = () => {
    const gtmOnShow = (name: string) => {
      gtmDatalayerPush({
        event: 'e_tooltip',
        contentName: name,
      } as ContentDataLayerObject);
    };
    return (
      <>
        {resultArray.map((data: ProcessedAudienceProps, _i: number) => (
          <React.Fragment key={data?.centerTypeName}>
            <p
              className={classnames(
                'text-theme-btn-link-text',
                'group',
                'relative',
                'inline-block',
                'cursor-pointer',
                'underline'
              )}
              data-tip
              data-for={`tooltip${_i}`}
              key={data?.centerTypeName}
              data-event="click focus"
            >
              {data?.centerTypeName}
            </p>
            {_i + 1 === resultArray.length ? '' : <span>,&nbsp;</span>}
            <ReactTooltip
              id={`tooltip${_i}`}
              globalEventOff="click"
              className="announcementV2page_tooltip max-w-[230px] sm:max-w-[300px]"
              type="dark"
              effect="solid"
              isCapture
              afterShow={() => {
                gtmOnShow(data?.centerTypeName);
              }}
            >
              <p className="text-sm font-bold leading-1 text-white">
                {data?.centerTypeName} {dictionary.AnnouncementV2_Recipients}
              </p>
              <p className="leading-3">
                {data.data.map(
                  (sdata: { salesForceRole: string; regionNameValue: string }, _j: number) => (
                    <span key={sdata?.salesForceRole || _j}>
                      {sdata?.salesForceRole}
                      {sdata?.regionNameValue ? ' - ' : ''}
                      {sdata?.regionNameValue}
                      {_j + 1 === data.data.length ? '' : <br />}
                    </span>
                  )
                )}
              </p>
            </ReactTooltip>
          </React.Fragment>
        ))}
      </>
    );
  };

  const pageTitleProps: PageTitleFieldProps = {
    fields: {
      pageTitle: {
        value: `AnnouncementV2`,
      },
    },
  };

  return (
    <div
      data-component="authorable/pages/announcementV2page"
      className={classnames('page-section-gap-closer', 'relative', 'z-10')}
    >
      <PageTitleWithField fields={pageTitleProps.fields} />
      <LayoutPageSectionShell theme="theme-inherit" padding="tiny">
        <h2 className={classnames('text-theme-heading-text', 'heading-lg', 'mb-2')}>
          <TextWithOptionalLineBreaks tag="h2" field={fields?.subject} editable />
        </h2>
        <div className={classnames('text-body', 'my-2')}>
          <span className={classnames('text-theme-btn-text')}>{publishDate}</span>
          <span className={classnames('mx-2')}>|</span>
          <span className={classnames('text-theme-btn-text')}>{getAudienceData()}</span>
        </div>
        <RichTextA11yWrapper
          className={classnames('text-body', 'mt-8', 'rte')}
          field={fields.body}
          editable
        />
      </LayoutPageSectionShell>
    </div>
  );
};
export default AnnouncementV2Page;
